import { supabase } from "@/integrations/supabase/client";
import type { User } from '@supabase/supabase-js';
import Logger from "@/utils/logger";
import * as Sentry from "@sentry/react";

export async function getCurrentSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    Logger.error('Error fetching session', error, { component: 'authService' });
    throw error;
  }
  
  return session;
}

export async function checkUserProfile(userId: string): Promise<boolean> {
  try {
    const { data: profile, error } = await supabase
      .from('user_profile_config')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    // "No rows" → just means the user hasn't created a profile yet.
    if (error && error.details !== 'The result contains 0 rows') {
      throw error;
    }

    return !!profile;
  } catch (err) {
    // Network / unknown issues → treat as "no profile" and move on.
    Logger.warn('Falling back to no-profile after fetch failure', {
      component: 'authService',
      userId,
      err
    });
    return false;
  }
}

export async function signIn(email: string, password: string) {
  Logger.info('Attempting sign in', { 
    component: 'authService',
    email 
  });
  
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) {
    Logger.error('Sign in failed', error, {
      component: 'authService',
      email
    });
    throw error;
  }
  
  Logger.info('Sign in successful', { 
    component: 'authService',
    email 
  });
}

export async function signOut() {
  Logger.info('Attempting sign out', { component: 'authService' });
  
  try {
    Sentry.setUser(null);
    const { error } = (await supabase.auth.signOut()) || {};
    if (error && error.message !== 'Auth session missing!') {
      Logger.error('Supabase sign out error', error, {
        component: 'authService'
      });
      throw error;
    }
    Logger.info('Sign out successful', { component: 'authService' });
  } catch (error) {
    Logger.error('Sign out failed', error as Error, {
      component: 'authService'
    });
    throw error;
  }
}

export function subscribeToAuthChanges(callback: (user: User | null) => void) {
  return supabase.auth.onAuthStateChange((_event, session) => {
    const user = session?.user || null;

    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.user_metadata?.full_name || user.email?.split('@')[0]
      });
    } else {
      Sentry.setUser(null);
    }

    callback(user);
  });
}

export function subscribeToUserChanges(userId: string | undefined, onDelete: () => void) {
  if (!userId) return { unsubscribe: () => {} };
  
  const channel = supabase
    .channel('user-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'users',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        if (payload.eventType === 'DELETE') {
          Logger.warn('User record deleted, triggering sign out', {
            component: 'authService',
            userId
          });
          onDelete();
        }
      }
    )
    .subscribe((status, err) => {
      if (status === 'CHANNEL_ERROR') {
        Logger.warn('WebSocket subscription error, continuing without real-time updates', {
          component: 'authService',
          userId,
          error: err
        });
      } else if (status === 'TIMED_OUT') {
        Logger.warn('WebSocket subscription timed out, continuing without real-time updates', {
          component: 'authService',
          userId
        });
      }
    });

  return {
    unsubscribe: () => {
      try {
        channel.unsubscribe();
      } catch {
        // Silently ignore unsubscribe errors
      }
    }
  };
}
